import { useMemo } from 'react';

import { Box, Button, Chip, Typography } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';

import { useClientFormProgressStyles } from './styles';

export type LineItemProps = {
  label: string;
  to?: string;
  attention?: boolean;
  completed?: boolean;
  score?: number;
  description?: string;
  referral?: boolean;
  referralFromUser?: string;
  referralContent?: string;
};

export const LineItem: React.VFC<LineItemProps> = ({ label, attention, completed, score, description, referral, referralFromUser, referralContent }) => {
  const { classes } = useClientFormProgressStyles();
  const l10n = useL10n();

  const referralDetails = useMemo(() => {

    const transformFullName = (fullName: string) => {
      const [ firstName, lastName ] = fullName.split(' ');

      return `${firstName.charAt(0)}. ${lastName}`;
    };

    return referralContent ? (
      <Box display="flex" alignItems="center">
        <AttachFile data-testid="note-attached-icon"/>
        <Typography data-testid="note-from-org-manager">{`Note from: ${transformFullName(referralFromUser)}`}</Typography>
      </Box>
    ) : (
      <Button disabled size="small">{l10n.dashboard.formProgress.noNote}</Button>
    );
  }, [ l10n.dashboard.formProgress.noNote, referralContent, referralFromUser ]);

  const action = useMemo(() => {
    if (completed) {
      if (score && description) {
        return <Typography><b>{score}</b> {description}</Typography>;
      }
      return <Typography>{l10n.dashboard.formProgress.completed}</Typography>;
    }
    if (referral) {
      return referralDetails;
    }

    return (
      <Button disabled size="small">{l10n.dashboard.formProgress.incomplete}</Button>
    );
  }, [ completed, description, l10n.dashboard.formProgress.completed, l10n.dashboard.formProgress.incomplete, referralDetails, referral, score ]);

  return (
    <Box display="flex" justifyContent="space-between">
      <Box display="flex" alignItems="center">
        <Typography className={classes.itemText} variant="body2" color="textPrimary">
          {label}
        </Typography>
        {attention && (
          <Box ml={2}>
            <Chip
              variant="outlined"
              label="Requires attention"
              size="small"
              classes={{
                root: classes.attentionChip,
                labelSmall: classes.attentionChipLabel
              }}
            />
          </Box>
        )}
      </Box>
      <Box>
        {action}
      </Box>
    </Box>
  );
};
